import type { Dispatch, SetStateAction } from 'react';
import type { SortType } from 'rsuite';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';
import type { DataGridColumnProps, DataGridProps } from '../DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../DataGridControl/DataGridControl';
import type { PageChangeFn } from '../Pagination/Pagination';

import { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { resizeColumn } from '../../lib/helpers';

import DataGrid from '../../components/DataGrid/DataGrid';

import useDebounceCallback from '../../hooks/useDebounceCallback';

import Loader from '../Loader/Loader';

interface CustomizableDataGridProps extends DataGridProps {
  /**
   * Function to handle columns change
   */
  setColumns?: Dispatch<SetStateAction<DataGridColumnProps[]>>;
  /**
   * Local storage key for sorting
   */
  sortingLocalStorageKey?: string;
  /**
   * Local storage key for grid options
   */
  optionsLocalStorageKey?: string;
  /**
   * Function to handle current page change
   */
  setCurrentPage?: PageChangeFn;
  /**
   * DataGrid no data info text
   */
  noDataInfoText?: string;
}

const CustomizableDataGrid = ({
  setColumns,
  sortingLocalStorageKey = '',
  optionsLocalStorageKey = '',
  setCurrentPage = EMPTY_FUNCTION,
  noDataInfoText,
  noDataInfo,
  onColumnSettingsChange,
  ...props
}: CustomizableDataGridProps) => {
  const isTablet = useMediaQuery({ minWidth: 768 });
  const [, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(
      sortingLocalStorageKey,
    );
  const [gridOptions, setGridOptions] = useLocalStorageState<
    DataGridColumnOptionsProps[]
  >(optionsLocalStorageKey);

  const handleSort = useCallback(
    (sortField?: string, sortOrder?: SortType) => {
      if (sortField && sortOrder) {
        setSort({ sortField, sortOrder });
      } else {
        removeSort();
      }
      setCurrentPage(1);
    },
    [removeSort, setCurrentPage, setSort],
  );

  const handleColumnResize = useCallback(
    (columnWidth: number, dataKey: string) =>
      resizeColumn(columnWidth, dataKey, gridOptions, setGridOptions),
    [gridOptions, setGridOptions],
  );

  const debouncedOnColumnResize = useDebounceCallback(handleColumnResize, 100);

  const handleColumnSettingsChange = useCallback(
    (updatedColumns: DataGridColumnProps[]) => {
      if (!setColumns) return;

      setColumns((currentColumns) => {
        const accessors = new Set(
          currentColumns.map((column) => column.accessor),
        );
        return updatedColumns.filter((column) =>
          accessors.has(column.accessor),
        );
      });
      const savedOptions = new Map(
        gridOptions?.map((option) => [option.colId, option]),
      );
      setGridOptions(
        updatedColumns.map(({ accessor, hide, width }) => ({
          ...savedOptions.get(accessor),
          colId: accessor,
          hide: !!hide,
          width: width ?? savedOptions.get(accessor)?.width,
        })),
      );

      onColumnSettingsChange?.(updatedColumns);
    },
    [gridOptions, onColumnSettingsChange, setColumns, setGridOptions],
  );

  return (
    <div className="relative w-full min-h-0 flex-1">
      <div
        className={twMerge(
          'w-full',
          isTablet ? 'h-[calc(100%-72px)]' : 'h-auto',
        )}
      >
        <DataGrid
          autoHeight={!isTablet}
          fillHeight={isTablet}
          {...props}
          onColumnSettingsChange={
            setColumns ? handleColumnSettingsChange : undefined
          }
          onSortColumn={handleSort}
          loadingIcon={<Loader size={'small'} type={'spinner-grid'} />}
          onResize={debouncedOnColumnResize}
          noDataInfo={
            noDataInfo ?? (
              <span className="flex items-center justify-center w-full h-full">
                {noDataInfoText}
              </span>
            )
          }
        />
      </div>
    </div>
  );
};

export default CustomizableDataGrid;
